import { useState, useCallback, useEffect } from "react";
import { FormikProps } from "formik";
import { LocationFormValues } from "../types";
import { useStores } from "../../../../stores/setup/use-store";
import { useOperationRuleLocationDrawing } from "./useOperationRuleLocationDrawing";
import { MacroRegion } from "../../../../enum/MacroRegion";
import { getMacroRegionOriginFromPolygon } from "../../../../util/macroRegion/getMacroRegionOriginFromPolygon";
import { extractCoordinatesFromGeoJSON } from "../../../ForecastChart/ForecastChartPanelMap/util/extractCoordinatesFromGeoJSON";

interface UseLocationGeofenceProps {
	formik: FormikProps<LocationFormValues>;
	isEditing: boolean;
}

export function useLocationGeofence({ formik, isEditing }: UseLocationGeofenceProps) {
	const {
		dataStores: { locationStore, regionStore, authStore, insightAlertStore, operationRuleLocationStore },
	} = useStores();

	const { handleDrawingMode: navigateToDrawingMode } = useOperationRuleLocationDrawing();

	const [areaSelectorVisible, setAreaSelectorVisible] = useState(false);
	const [isDropZoneVisible, setIsDropZoneVisible] = useState(false);
	const [isGeofenceLoadedByGeoJson, setIsGeofenceLoadedByGeoJson] = useState(false);

	const convertMacroRegionToId = useCallback(
		(macroRegion: MacroRegion | null): number | undefined => {
			if (!macroRegion) return undefined;

			const macroRegionData = regionStore.macroRegions.find(
				(mr) => mr.origin === macroRegion
			);
			return macroRegionData?.id;
		},
		[regionStore.macroRegions]
	);

	useEffect(() => {
		if (locationStore.areas.length === 0) {
			locationStore.getAllLocationsAPI();
		}
	}, [locationStore]);

	useEffect(() => {
		if (isEditing && operationRuleLocationStore.operationRuleLocation) {
			const { locationId } = operationRuleLocationStore.operationRuleLocation;
			if (locationId && locationId > 0) {
				setAreaSelectorVisible(true);
			} else {
				setAreaSelectorVisible(false);
			}
		} else {
			setAreaSelectorVisible(false);
		}
	}, [isEditing, operationRuleLocationStore.operationRuleLocation]);

	useEffect(() => {
		const locationId = formik.values.locationId;
		const geofence = formik.values.geofence;

		// Only auto-load geofence when editing and both locationId exists and geofence is empty
		// This prevents interference with manual selection via handleAreaSelection
		if (
			isEditing &&
			locationId &&
			locationId > 0 &&
			locationStore.areas.length > 0 &&
			(!geofence || geofence.length === 0)
		) {
			const selectedArea = locationStore.areas.find((area) => area.id === locationId);
			if (selectedArea && selectedArea.coordinates) {
				formik.setFieldValue("geofence", selectedArea.coordinates);
			}
		}
	}, [isEditing, formik.values.locationId, locationStore.areas, formik.setFieldValue]);

	useEffect(() => {
		if (!isEditing || !operationRuleLocationStore.operationRuleLocation) {
			setIsDropZoneVisible(false);
			setIsGeofenceLoadedByGeoJson(false);
			insightAlertStore.setIsGeofenceInvalid(false);
		}
	}, [isEditing, operationRuleLocationStore.operationRuleLocation, insightAlertStore]);

	const handleDrawingMode = useCallback(() => {
		const formValues: LocationFormValues = {
			name: formik.values.name,
			description: formik.values.description,
			stationRegion: formik.values.stationRegion,
			regionId: formik.values.regionId,
			macroRegionId: formik.values.macroRegionId,
			locationId: formik.values.locationId,
			geofence: formik.values.geofence,
		};

		navigateToDrawingMode(formValues, isEditing);
	}, [formik.values, navigateToDrawingMode, isEditing]);

	const handleGeoJsonLoaded = useCallback(
		(geojson: any) => {
			if (!geojson) return;

			setIsGeofenceLoadedByGeoJson(true);

			const coordinates = extractCoordinatesFromGeoJSON(geojson);
			const flattenedCoordinates = coordinates.length > 0 && Array.isArray(coordinates[0])
				? coordinates.flat()
				: coordinates;

			const macroRegion = getMacroRegionOriginFromPolygon(
				coordinates,
				regionStore.macroRegions,
				authStore.userMarcroRegions as MacroRegion[]
			);

			formik.setFieldValue("geofence", flattenedCoordinates);
			formik.setFieldValue("locationId", undefined);
			setIsDropZoneVisible(false);

			if (macroRegion) {
				const macroRegionId = convertMacroRegionToId(macroRegion);
				if (macroRegionId) {
					formik.setFieldValue("macroRegionId", macroRegionId);
				}
				insightAlertStore.setIsGeofenceInvalid(false);
			} else {
				insightAlertStore.setIsGeofenceInvalid(true);
			}
		},
		[formik, regionStore.macroRegions, authStore.userMarcroRegions, insightAlertStore, convertMacroRegionToId]
	);

	const clearGeofence = useCallback(() => {
		formik.setFieldValue("geofence", undefined);
		formik.setFieldValue("locationId", undefined);
		formik.setFieldValue("macroRegionId", undefined);
		setAreaSelectorVisible(false);
		setIsDropZoneVisible(false);
		setIsGeofenceLoadedByGeoJson(false);
		insightAlertStore.setIsGeofenceInvalid(false);
	}, [formik, insightAlertStore]);

	const handleEnableAreaSelector = useCallback((value: boolean) => {
		setAreaSelectorVisible(value);
		if (value) {
			formik.setFieldValue("geofence", undefined);
		}
	}, [formik]);

	const handleAreaSelection = useCallback(
		(selectedAreaId: number, areas: any[], setFieldValue: (field: string, value: any) => void) => {
			const selectedArea = areas.find((area) => area.id === selectedAreaId);

			if (selectedArea) {
				setFieldValue("locationId", selectedAreaId);

				if (selectedArea.coordinates) {
					setFieldValue("geofence", selectedArea.coordinates);

					const coordinatesArray = Array.isArray(selectedArea.coordinates[0])
						? selectedArea.coordinates
						: [selectedArea.coordinates];

					const macroRegion = getMacroRegionOriginFromPolygon(
						coordinatesArray,
						regionStore.macroRegions,
						authStore.userMarcroRegions as MacroRegion[]
					);

					if (macroRegion) {
						const macroRegionId = convertMacroRegionToId(macroRegion);
						if (macroRegionId) {
							setFieldValue("macroRegionId", macroRegionId);
						}
					}
				} else {
					setFieldValue("geofence", undefined);
				}
			}
		},
		[regionStore.macroRegions, authStore.userMarcroRegions, convertMacroRegionToId]
	);

	return {
		areaSelectorVisible,
		isDropZoneVisible,
		isGeofenceLoadedByGeoJson,
		isGeofenceInvalid: insightAlertStore.isGeofenceInvalid,
		selectedGeofence: formik.values.geofence || [],
		areas: locationStore.areas,
		handleDrawingMode,
		handleGeoJsonLoaded,
		clearGeofence,
		handleEnableAreaSelector,
		handleAreaSelection,
		setIsGeofenceLoadedByGeoJson,
		setIsDropZoneVisible,
	};
}
